import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';

/** Material UI Packages. */
import { Dialog, Box, Button, Tooltip, Typography } from '@mui/material';
import { DataGridPremium, GridToolbarContainer, GridActionsCellItem } from '@mui/x-data-grid-premium';
import { randomId } from '@mui/x-data-grid-generator';

/** Material UI Icons. */
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweepOutlined';

/** Redux Packages. */
import { useDispatch } from 'react-redux';
import { setSnackbar } from 'shared/store/slices/snackBarSlice';

/** Internal Components. */
import { PaperComponent } from 'shared/ui/DraggableDialog';
import DialogHeader from 'shared/ui/DialogHeader';
import { CustomAutocompleteCell_TradePartner } from 'shared/ui/RenderCell';
import { callAPI } from 'shared/api/callApi';
import { formatDateToDateString } from 'shared/lib/dateFormatter';
import { tradePartnerValueFormatter } from 'shared/lib/datagridHelpers';
import { createDateColumn, isNegativeAmount, negativeValueMessage, rejectNegativeEditProps } from './DutyEntriesDef';
import {
    createClientPasteParser,
    createDatePasteParser,
    createNumberPasteParser,
    formatMismatchMessage,
} from './bulkPasteParsers';

/** Number of blank rows laid out when the dialog opens. */
const INITIAL_ROW_COUNT = 8;

/**
 * How many entries can be registered at once — must match the server limit
 * (_MAX_BULK_ENTRIES) so the operator finds out before submitting rather than being
 * rejected afterwards.
 */
const MAX_BULK_ROWS = 200;

/** Fields the operator fills in on the bulk screen — the same ones editable in the main table. */
const BULK_FIELDS = [
    { key: 'il_trade_partner', label: 'Client', type: 'client' },
    { key: 'import_date', label: 'Import Date', type: 'date' },
    { key: 'opus_reference', label: 'Opus Ref' },
    { key: 'entry_number', label: 'Entry No.' },
    { key: 'contract_number', label: 'Contract No.' },
    { key: 'entry_invoice_date', label: 'Entry INV Date', type: 'date' },
    { key: 'entry_fee', label: 'Entry Fee', type: 'number' },
    { key: 'trucking_invoice_date', label: 'Trucking INV Date', type: 'date' },
    { key: 'trucking_fee', label: 'Trucking Fee', type: 'number' },
    { key: 'reference', label: 'Reference' },
];

const DATE_FIELDS = BULK_FIELDS.filter((field) => field.type === 'date').map((field) => field.key);

/** 수수료 칸 — 지출 금액이라 음수가 될 수 없다. */
const FEE_FIELDS = BULK_FIELDS.filter((field) => field.type === 'number');

/** SAVE 직전에 음수 수수료가 남은 행의 화면 번호(No. 칸)를 모아 담당자에게 알려준다. */
export const collectNegativeFeeScreenRows = (filledRows) =>
    filledRows
        .filter(({ row }) => FEE_FIELDS.some((field) => isNegativeAmount(row[field.key])))
        .map(({ screenRow }) => screenRow);

const createBlankRow = () => {
    const row = { id: randomId() };
    BULK_FIELDS.forEach((field) => {
        row[field.key] = null;
    });
    return row;
};

const createBlankRows = (count = INITIAL_ROW_COUNT) => Array.from({ length: count }, () => createBlankRow());

const isBlankValue = (value) => value === '' || value === null || value === undefined;

/** A row the operator never touched — blank rows are pre-laid out, so drop them from the request. */
const isRowEmpty = (row) => BULK_FIELDS.every((field) => isBlankValue(row[field.key]));

/**
 * Registers many import duty entries from a single screen.
 *
 * One row is one entry. This serves the flow where an operator pastes in a list already
 * organized in Excel and registers it in one go, replacing the work of pressing NEW per entry
 * and filling the cells in. Line items are added afterwards, from each entry's detail panel.
 *
 * The server handles this request in a single transaction — if even one row cannot be saved,
 * everything is rolled back and the message names the row that blocked it.
 */
export default function BulkDutyEntryDialog({
    open,
    onClose,
    clientList,
    userEmail,
    getAccessTokenSilently,
    onCreated,
}) {
    const dispatch = useDispatch();

    const [rows, setRows] = useState(createBlankRows);
    const [rowSelectionModel, setRowSelectionModel] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    const handleAddRow = useCallback(() => {
        setRows((prev) => [...prev, createBlankRow()]);
    }, []);

    const handleDeleteRow = useCallback((id) => {
        setRows((prev) => prev.filter((row) => row.id !== id));
        setRowSelectionModel((prev) => prev.filter((rowId) => rowId !== id));
    }, []);

    const handleDeleteSelected = useCallback(() => {
        if (rowSelectionModel.length === 0) return;
        setRows((prev) => prev.filter((row) => !rowSelectionModel.includes(row.id)));
        setRowSelectionModel([]);
    }, [rowSelectionModel]);

    /**
     * Tells the operator when a pasted value does not match the field's format.
     * (e.g. a date landing in the Client column, letters in a fee column — the value is not
     * taken and the previous one is kept)
     */
    const notifyFormatMismatch = useCallback(
        (label) => {
            dispatch(setSnackbar({ open: true, message: formatMismatchMessage(label), severity: 'warning' }));
        },
        [dispatch],
    );

    const processRowUpdate = useCallback(
        (newRow, oldRow) => {
            // 붙여넣기는 셀 편집 검증을 거치지 않으므로 반영 직전에 한 번 더 거른다
            const negativeFee = FEE_FIELDS.find((field) => isNegativeAmount(newRow[field.key]));
            if (negativeFee) {
                dispatch(
                    setSnackbar({ open: true, message: negativeValueMessage(negativeFee.label), severity: 'error' }),
                );
                return oldRow;
            }
            setRows((prev) => prev.map((row) => (row.id === newRow.id ? newRow : row)));
            return newRow;
        },
        [dispatch],
    );

    const handleClose = useCallback(() => {
        const hasData = rows.some((row) => !isRowEmpty(row));
        if (hasData && !window.confirm('Unsaved changes will be lost. Do you want to continue?')) return;
        setRows(createBlankRows());
        setRowSelectionModel([]);
        onClose();
    }, [rows, onClose]);

    /** Maps one row on screen to the one entry the server expects. */
    const buildPayloadRow = useCallback((row) => {
        const payload = {};
        BULK_FIELDS.forEach(({ key }) => {
            const value = row[key];
            if (isBlankValue(value)) return;
            if (key === 'il_trade_partner') {
                // The partner must be an {id, name} object for the server to pull the FK (il_trade_partner_id)
                payload[key] = typeof value === 'object' ? { id: value.id, name: value.name ?? '' } : value;
            } else if (DATE_FIELDS.includes(key)) {
                payload[key] = value instanceof Date ? formatDateToDateString(value) : value;
            } else {
                payload[key] = value;
            }
        });
        return payload;
    }, []);

    const handleSubmit = useCallback(() => {
        // Keep each filled row's on-screen number (the No. column): the server numbers only
        // the submitted rows, so its "Row N" must be translated back for the operator.
        const filledRows = rows
            .map((row, index) => ({ row, screenRow: index + 1 }))
            .filter(({ row }) => !isRowEmpty(row));
        if (filledRows.length === 0) {
            dispatch(setSnackbar({ open: true, message: 'Please enter at least one row.', severity: 'warning' }));
            return;
        }
        if (filledRows.length > MAX_BULK_ROWS) {
            dispatch(
                setSnackbar({
                    open: true,
                    message: `Up to ${MAX_BULK_ROWS} entries can be registered at once (currently ${filledRows.length}). Please split the batch.`,
                    severity: 'warning',
                }),
            );
            return;
        }

        // 서버도 같은 검증으로 거절하지만, 제출 전에 어느 행인지 화면 번호로 먼저 알려준다
        const negativeRows = collectNegativeFeeScreenRows(filledRows);
        if (negativeRows.length > 0) {
            dispatch(
                setSnackbar({
                    open: true,
                    message: `${negativeValueMessage('Entry Fee or Trucking Fee')} (row ${negativeRows.join(', ')})`,
                    severity: 'error',
                }),
            );
            return;
        }

        const payload = filledRows.map(({ row }) => buildPayloadRow(row));

        callAPI({
            method: 'post',
            module: 'duty_calculator',
            component: 'duty_entry',
            // All or nothing — the server handles this in a single transaction
            type: 'multiple',
            getAccessTokenSilently,
            email: userEmail,
            obj: payload,
            onInit: () => setSubmitting(true),
            onFinal: () => setSubmitting(false),
            onSuccess: () => {
                dispatch(
                    setSnackbar({
                        open: true,
                        message: `${payload.length} duty entr${payload.length === 1 ? 'y' : 'ies'} registered.`,
                        severity: 'success',
                    }),
                );
                setRows(createBlankRows());
                setRowSelectionModel([]);
                onCreated?.();
                onClose();
            },
            onError: (error) => {
                const message = error?.message ?? 'Failed to register duty entries.';
                // The server's "Row N" counts only the submitted rows — rewrite N to the
                // on-screen row number (blank rows in between shift the two apart).
                const displayed = message.replace(/^Row (\d+):/, (prefix, n) => {
                    const screenRow = filledRows[Number(n) - 1]?.screenRow;
                    return screenRow ? `Row ${screenRow}:` : prefix;
                });
                dispatch(setSnackbar({ open: true, message: displayed, severity: 'error' }));
            },
        });
    }, [rows, buildPayloadRow, dispatch, getAccessTokenSilently, userEmail, onCreated, onClose]);

    const columns = useMemo(
        () => [
            {
                field: 'rowNumber',
                headerName: 'No.',
                width: 48,
                align: 'center',
                headerAlign: 'center',
                editable: false,
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                // The number the server's "Row N" message points at — sorting is disabled
                // on every column, so the visible order always matches the submitted order.
                renderCell: (params) => params.api.getRowIndexRelativeToVisibleRows(params.id) + 1,
            },
            ...BULK_FIELDS.map((field) => {
                const base = {
                    field: field.key,
                    headerName: field.label,
                    align: 'center',
                    headerAlign: 'center',
                    flex: 1,
                    editable: true,
                    sortable: false,
                    disableColumnMenu: true,
                };

                if (field.type === 'client') {
                    return {
                        ...base,
                        /** Copy (Ctrl+C) emits just the partner name as plain text. */
                        valueFormatter: tradePartnerValueFormatter,
                        /** Turns a pasted name back into a partner — keeps the previous value if unrecognized. */
                        valueParser: createClientPasteParser({
                            options: clientList,
                            onMismatch: notifyFormatMismatch,
                        }),
                        // Display reuses the name built by valueFormatter — a custom renderCell here
                        // would not fill the cell height, so it would sit at the top unlike other columns.
                        renderEditCell: (params) => (
                            <CustomAutocompleteCell_TradePartner params={params} options={clientList} />
                        ),
                    };
                }

                if (field.type === 'date') {
                    // Uses the same definition as the main table's date columns, so display format,
                    // paste handling and stored value never diverge between the two screens.
                    return createDateColumn({
                        field: field.key,
                        headerName: field.label,
                        flex: 1,
                        sortable: false,
                        disableColumnMenu: true,
                        // Every column is editable on the bulk screen, so the underline marker is redundant
                        headerClassName: undefined,
                        // Do not drop unreadable values silently — say which column rejected it
                        pastedValueParser: createDatePasteParser({
                            label: field.label,
                            onMismatch: notifyFormatMismatch,
                        }),
                    });
                }

                if (field.type === 'number') {
                    return {
                        ...base,
                        type: 'number',
                        // 수수료는 음수가 될 수 없다 — 입력 즉시 셀이 오류 상태가 되어 저장이 막힌다
                        preProcessEditCellProps: rejectNegativeEditProps,
                        pastedValueParser: createNumberPasteParser({
                            label: field.label,
                            onMismatch: notifyFormatMismatch,
                        }),
                    };
                }

                return base;
            }),
            {
                field: 'actions',
                type: 'actions',
                headerName: '',
                width: 50,
                getActions: ({ id }) => [
                    <Tooltip title="Delete row" key="delete">
                        <GridActionsCellItem
                            icon={<DeleteIcon />}
                            label="Delete"
                            onClick={() => handleDeleteRow(id)}
                            color="inherit"
                        />
                    </Tooltip>,
                ],
            },
        ],
        [clientList, handleDeleteRow, notifyFormatMismatch],
    );

    const Toolbar = useCallback(
        () => (
            <GridToolbarContainer sx={{ justifyContent: 'space-between', px: 1, mb: 0.5 }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button sx={{ color: '#003a52', fontSize: '11px' }} startIcon={<AddIcon />} onClick={handleAddRow}>
                        Add Row
                    </Button>
                    <Button
                        sx={{ color: '#a33', fontSize: '11px' }}
                        startIcon={<DeleteSweepIcon />}
                        onClick={handleDeleteSelected}
                        disabled={rowSelectionModel.length === 0}
                    >
                        Delete Selected
                    </Button>
                </Box>
                <Typography sx={{ fontSize: '11px', color: '#777', pr: 1 }}>
                    One row = one duty entry. Blank rows are ignored. Items are added afterwards from each entry&apos;s
                    detail panel.
                </Typography>
            </GridToolbarContainer>
        ),
        [handleAddRow, handleDeleteSelected, rowSelectionModel],
    );

    return (
        <Dialog
            maxWidth="xl"
            fullWidth
            open={open}
            onClose={handleClose}
            PaperProps={{ sx: { borderRadius: '18px' } }}
            PaperComponent={PaperComponent}
        >
            <Box sx={{ height: 650, display: 'flex', flexDirection: 'column' }}>
                <DialogHeader
                    cursor
                    title="BULK DUTY ENTRY"
                    onClose={handleClose}
                    onSave={handleSubmit}
                    saveDisabled={submitting}
                />
                <Box sx={{ flex: 1, p: 1.5, minHeight: 0 }}>
                    <DataGridPremium
                        rows={rows}
                        columns={columns}
                        rowHeight={33}
                        columnHeaderHeight={33}
                        editMode="cell"
                        checkboxSelection
                        disableRowSelectionOnClick
                        rowSelectionModel={rowSelectionModel}
                        onRowSelectionModelChange={setRowSelectionModel}
                        processRowUpdate={processRowUpdate}
                        onProcessRowUpdateError={(error) => console.error(error)}
                        cellSelection
                        hideFooter
                        slots={{ toolbar: Toolbar }}
                        sx={{
                            height: '100%',
                            fontSize: 12,
                            borderRadius: '10px',
                            // 잘못된 입력(음수 등)으로 오류 상태가 된 편집 셀을 빨간 테두리로 드러낸다
                            '& .MuiDataGrid-cell--editing .Mui-error': {
                                outline: '1.5px solid #d32f2f',
                                outlineOffset: '-1.5px',
                                backgroundColor: 'rgba(211, 47, 47, 0.08)',
                            },
                            '& .MuiDataGrid-columnHeader': { backgroundColor: '#dfdfdf' },
                            '& .MuiDataGrid-columnHeaderTitleContainer': {
                                justifyContent: 'center',
                                fontWeight: 'bold',
                            },
                            '& .MuiDataGrid-cell': { border: '0.5px solid #dfdfdf' },
                        }}
                    />
                </Box>
            </Box>
        </Dialog>
    );
}
